import React, { useEffect, useState } from "react";
import { logger } from "@/lib/logger";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export default function PWAFeatures() {
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);
  const [pendingCount, setPendingCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  const isPWA =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

  const isIOSSafari =
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream &&
    !isPWA;

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "SYNC_NOW" });
      }
    };
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Service worker message listener
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handleMessage = (event) => {
      if (event.data?.type === "PENDING_COUNT") setPendingCount(event.data.count);
      if (event.data?.type === "SYNC_COMPLETE") setPendingCount(0);
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "GET_PENDING_COUNT" });
    }
    return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, []);

  // Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!localStorage.getItem("pwa-install-dismissed")) {
        setTimeout(() => setShowInstallPrompt(true), 6000);
      }
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOSSafari && !localStorage.getItem("ios-install-shown")) {
      setTimeout(() => setShowIOSInstructions(true), 6000);
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isIOSSafari]);

  // Notification permission prompt
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "default" && !localStorage.getItem("notification-prompt-dismissed")) {
      setTimeout(() => setShowNotificationPrompt(true), 12000);
    }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    localStorage.setItem("pwa-install-dismissed", "true");
  };

  const dismissIOSInstructions = () => {
    setShowIOSInstructions(false);
    localStorage.setItem("ios-install-shown", "true");
  };

  const dismissNotificationPrompt = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem("notification-prompt-dismissed", "true");
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) return;
    const permission = await Notification.requestPermission();
    setShowNotificationPrompt(false);
    if (permission === "granted") {
      try {
        if ("serviceWorker" in navigator && "PushManager" in window) {
          const reg = await navigator.serviceWorker.ready;
          await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              "BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U"
            ),
          });
        }
        // Show a sample local notification immediately
        new Notification("AusumLog notifications enabled", {
          body: "We'll remind you to log your activities daily.",
          icon: "/icons/icon-192x192.png",
        });
      } catch (e) {
        logger.warn("Push subscribe failed:", e);
      }
    }
  };

  return (
    <>
      {/* Offline banner */}
      {isOffline && (
        <div
          data-testid="offline-banner"
          className="fixed top-0 left-0 right-0 z-[10000] bg-rose-500 text-white py-2 px-4 text-sm font-medium text-center slide-down flex items-center justify-center gap-2"
        >
          <i className="fas fa-cloud-arrow-down"></i>
          You&apos;re offline{pendingCount > 0 ? ` · ${pendingCount} pending` : ""}
        </div>
      )}

      {/* Install prompt modal */}
      {showInstallPrompt && (
        <Modal onClose={dismissInstallPrompt} testId="install-prompt-modal">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-blue-500/15 flex items-center justify-center mb-4">
              <img src="/icons/icon-192x192.png" alt="AusumLog" className="w-14 h-14 rounded-xl" />
            </div>
            <h3 className="text-xl font-bold text-white mb-1">Install AusumLog</h3>
            <p className="text-slate-400 text-sm mb-5">
              Add to home screen for offline access, push notifications, and a faster, app-like experience.
            </p>
            <ul className="text-left space-y-2 mb-5">
              <Benefit icon="fa-bolt" text="Instant load on every visit" />
              <Benefit icon="fa-wifi-slash" text="Works offline — auto-syncs later" />
              <Benefit icon="fa-bell" text="Daily reminder push notifications" />
            </ul>
            <button
              onClick={handleInstall}
              data-testid="install-app-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl btn-press flex items-center justify-center gap-2"
            >
              <i className="fas fa-download"></i> Install App
            </button>
            <button
              onClick={dismissInstallPrompt}
              className="w-full text-slate-500 hover:text-slate-300 mt-2 py-2 text-sm"
            >
              Maybe later
            </button>
          </div>
        </Modal>
      )}

      {/* iOS install instructions */}
      {showIOSInstructions && (
        <Modal onClose={dismissIOSInstructions} testId="ios-install-modal">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-blue-500/15 flex items-center justify-center mb-4">
              <i className="fas fa-mobile-screen text-3xl text-blue-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">Add to Home Screen</h3>
            <p className="text-slate-400 text-sm mb-5">Install AusumLog on your iPhone for the best experience.</p>
            <div className="space-y-3 text-left mb-5">
              <Step n="1" text={<>Tap the <i className="fas fa-arrow-up-from-bracket text-blue-400"></i> Share button</>} />
              <Step n="2" text='Scroll down and tap "Add to Home Screen"' />
              <Step n="3" text='Tap "Add" to install' />
            </div>
            <button
              onClick={dismissIOSInstructions}
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold btn-press"
            >
              Got it!
            </button>
          </div>
        </Modal>
      )}

      {/* Notification prompt */}
      {showNotificationPrompt && (
        <Modal onClose={dismissNotificationPrompt} testId="notification-modal">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-amber-500/15 flex items-center justify-center mb-4">
              <i className="fas fa-bell text-3xl text-amber-400"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">Stay on track!</h3>
            <p className="text-slate-400 text-sm mb-5">
              Enable notifications to get daily reminders to log your activities.
            </p>
            <ul className="text-left space-y-2 mb-5">
              <Benefit icon="fa-clock" text="Daily activity reminders" />
              <Benefit icon="fa-sparkles" text="Summary-ready alerts" />
            </ul>
            <button
              onClick={requestNotificationPermission}
              data-testid="enable-notifications-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-white py-3 rounded-xl font-semibold btn-press flex items-center justify-center gap-2"
            >
              <i className="fas fa-bell"></i> Enable Notifications
            </button>
            <button onClick={dismissNotificationPrompt} className="w-full text-slate-500 hover:text-slate-300 mt-2 py-2 text-sm">
              Not now
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 fade-in"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-2xl"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-slate-700 text-slate-400 hover:text-slate-200 flex items-center justify-center"
          aria-label="Close"
        >
          <i className="fas fa-xmark"></i>
        </button>
        {children}
      </div>
    </div>
  );
}

function Benefit({ icon, text }) {
  return (
    <li className="flex items-center gap-3 text-slate-200 text-sm">
      <span className="w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center text-blue-300">
        <i className={`fas ${icon}`}></i>
      </span>
      {text}
    </li>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-center gap-3 text-slate-200 text-sm">
      <span className="w-7 h-7 rounded-full bg-blue-500 text-white font-bold flex items-center justify-center text-xs">{n}</span>
      <div>{text}</div>
    </div>
  );
}
